import { useState, useEffect } from 'react'
import {
  addDays,
  isBefore,
  isAfter,
  format,
  Locale,
  isSameDay,
} from 'date-fns'
import { enGB } from 'date-fns/locale'
import {
  normalizeDate,
  daysInWeek,
  daysInMonth,
  isExtraDay,
  getFirstDay,
  getCalendarFormat as getNextCalendarFormat,
  previousMonth,
  nextMonth,
  previousWeek,
  nextWeek,
} from '../utils/date'
import { UseCalendarParams, CalendarFormat, DaysOfWeek } from './types'
import { clamp, getEventsDateMap } from '../utils/helpers'

export const useCalendar = ({
  initialDay = new Date(),
  initialFormat = CalendarFormat.month,
  includeInvisibleDays,
  startingDayOfWeek = DaysOfWeek.sunday,
  availableCalendarFormats = [CalendarFormat.month, CalendarFormat.week],
  selectedDayCallback,
  weekendDays = ['saturday', 'sunday'],
  events,
}: UseCalendarParams) => {
  const [locale, setLocale] = useState<Locale>(enGB)
  const [visibleDaysState, setVisibleDaysState] = useState<Date[]>([])
  const [focusedDay, setFocusedDayState] = useState<Date>(initialDay || new Date())
  const [showInvisibleDays, setShowInvisibleDays] = useState<boolean>(!!includeInvisibleDays)
  const [calendarFormat, setCalendarFormat] = useState<CalendarFormat>(initialFormat)
  const [selectedDay, setSelectedDayState] = useState<Date>(initialDay || new Date())
  const [pageId, setPageId] = useState<number>(0)
  const visibleDays = () => calendarFormat === CalendarFormat.month && !showInvisibleDays
    ? visibleDaysState.filter((day: Date) => !isExtraDay(day, focusedDay))
    : visibleDaysState

  const _getVisibleDays = () => {
    if (calendarFormat === CalendarFormat.month) {
      return daysInMonth(focusedDay, startingDayOfWeek)
    }
    if (calendarFormat === CalendarFormat.twoWeeks) {
      return [...daysInWeek(focusedDay, startingDayOfWeek), ...daysInWeek(addDays(focusedDay, 7), startingDayOfWeek)]
    }
    return daysInWeek(focusedDay, startingDayOfWeek)
  }
  /// Update Locale that the calendar should run in
  const updateLocale = (newLocale: Locale) => {
    setLocale(newLocale)
  }

  /// Sets displayed month/year without changing the currently selected day.
  const setFocusedDay = (value: Date) => {
    setFocusedDayState(normalizeDate(value))
  }

  const _updateVisibleDays = (isProgrammatic: boolean) => {
    if (calendarFormat !== CalendarFormat.twoWeeks || isProgrammatic) {
      const newDays = _getVisibleDays()
      // update visible days only if something was changed
      if (
        newDays.length !== visibleDaysState.length
        || newDays.some((date, index) => !isSameDay(date, visibleDaysState[index]))
      ) {
        setVisibleDaysState(newDays)
      }
    }
  }

  useEffect(() => {
    _updateVisibleDays(true)
  }, [focusedDay])

  /// Sets selected day to a given `value`.
  /// Use `runCallback: true` if this should trigger `OnDaySelected` callback.
  const setSelectedDay = (
    value: Date,
    isProgrammatic: boolean = true,
    animate: boolean = true,
    runCallback: boolean = false,
  ): void => {
    const normalizedDate = normalizeDate(value);

    if (animate) {
      if (isBefore(normalizedDate, getFirstDay(focusedDay, visibleDays(), false, calendarFormat))) {
        decrementPage();
      } else if (isAfter(normalizedDate, getFirstDay(focusedDay, visibleDays(), false, calendarFormat))) {
        incrementPage();
      }
    }
    setSelectedDayState(normalizedDate);
    setFocusedDay(normalizedDate);

    if (isProgrammatic && runCallback && !!selectedDayCallback) {
      selectedDayCallback(normalizedDate);
    }
  }

  const decrementPage = () => {
    setPageId(pageId - 1)
  }

  const incrementPage = () => {
    setPageId(pageId + 1)
  }

  const swipeCalendarFormat = (isSwipeUp: boolean) => {
    let id: number = availableCalendarFormats.indexOf(calendarFormat);

    // Order of CalendaravailableCalendarFormats must be from biggest to smallest,
    // eg.: [month, twoWeeks, week]
    if (isSwipeUp) {
      id = clamp(0, availableCalendarFormats.length - 1, id + 1);
    } else {
      id = clamp(0, availableCalendarFormats.length - 1, id - 1);
    }
    setCalendarFormat(availableCalendarFormats[id]);
  }

  const toggleCalendarFormat = () => {
    setCalendarFormat(getNextCalendarFormat(availableCalendarFormats, calendarFormat));
  }

  useEffect(() => {
    setVisibleDaysState(_getVisibleDays())
  }, [])

  useEffect(() => {
    setVisibleDaysState(_getVisibleDays())
  }, [calendarFormat])
  
  const selectPrevious = () => {
    if (calendarFormat == CalendarFormat.month) {
      selectPreviousMonth();
    } else if (calendarFormat == CalendarFormat.twoWeeks) {
      selectPreviousTwoWeeks();
    } else {
      selectPreviousWeek();
    }

    decrementPage();
  }

  const selectNext = () => {
    if (calendarFormat == CalendarFormat.month) {
      selectNextMonth();
    } else if (calendarFormat == CalendarFormat.twoWeeks) {
      selectNextTwoWeeks();
    } else {
      selectNextWeek();
    }
    incrementPage();
  }

  const selectPreviousMonth = (): void => {
    const nextDate = previousMonth(focusedDay)
    setSelectedDay(nextDate)
    setFocusedDay(nextDate);
  }

  const selectNextMonth = (): void => {
    const nextDate = nextMonth(focusedDay)
    setSelectedDay(nextDate)
    setFocusedDay(nextDate);
  }

  const selectPreviousTwoWeeks = (): void => {
    if (visibleDays().slice(0, 7).includes(focusedDay)) {
      // in top row
      setFocusedDay(previousWeek(focusedDay));
    } else {
      // in bottom row OR not visible
      setFocusedDay(previousWeek(addDays(focusedDay, 7 * -1)));
    }
  }

  const selectNextTwoWeeks = ():void => {
    if (!visibleDaysState.slice(8, visibleDaysState.length - 1).includes(focusedDay)) {
      // not in bottom row [eg: in top row OR not visible]
      setFocusedDay(nextWeek(focusedDay));
    }
  }

  const selectPreviousWeek = ():void => {
    const nextDate = previousWeek(focusedDay)
    setSelectedDay(nextDate)
    setFocusedDay(nextDate);
  }

  const selectNextWeek = ():void => {
    const nextDate = nextWeek(focusedDay)
    setSelectedDay(nextDate)
    setFocusedDay(nextDate);
  }

  const getDaysOfWeek = () => visibleDaysState.slice(0, 7).map((day: Date) => format(day, 'EEEE', {locale}))

  return {
    calendarFormat,
    focusedDay,
    setFocusedDay,
    setCalendarFormat,
    visibleDays: visibleDaysState,
    setShowInvisibleDays,
    visibleEvents: getEventsDateMap(visibleDaysState, events),
    selectedDay,
    setSelectedDay,
    daysOfWeek: getDaysOfWeek(),
    swipeCalendarFormat,
    toggleCalendarFormat,
    selectPrevious,
    selectNext,
    updateLocale,
    weekendDays,
    disableSwiping: false,
  }
}

export type CalendarController = ReturnType<typeof useCalendar>
